import {Button, Card, Form, Image, Modal as BootstrapModal} from "react-bootstrap";
import {FaInfoCircle,} from 'react-icons/fa';
import React, {useState} from "react";
import {useWarrantyChange} from "../../../hooks/useWarrantyChange";
import {useWarrantiesQuery} from "../../../hooks/useWarrantiesQuery";
import {WarrantyModel} from "../../../models/domain/warrantyModel";
import {catalogName} from "../../../utils/helpers";
import {useWarrantyModal} from "../../../redux/useWarrantyModal";

export const Warranty = () => {
    const {model: {warranties}} = useWarrantiesQuery();
    const [show, setShow] = useState<boolean>(false);

    // If there are no warranties, return an empty fragment
    if (warranties.length <= 0) return <></>;

    return (
        <Card body>
            <div className="d-flex align-items-start justify-content-between">
                <div className="mb-intro">
                    <Button variant="link" className="p-0" onClick={() => setShow(true)} aria-label="Add Product Protection Information Popup" role="button">
                        <strong>
                            Add Product Protection<FaInfoCircle className="ms-1" aria-hidden="true"/>
                        </strong>
                    </Button>
                    <br/><small className="text-muted">*Valid in US only</small>
                </div>
                <div>
                    <Image src="https://static.commerceplatform.services/content/img/mulberry-warranty.jpg"
                           className="img-responsive"
                           alt="Mulberry Warranty"
                           height={22}
                           width={75}/>
                </div>
            </div>
            <div className="warranty-row d-flex align-items center mt-2">
                {warranties.map((model, index) => <Content key={index} model={model}/>)}
            </div>
            <Modal show={show} onHide={() => setShow(false)}/>
        </Card>
    )
}

const Content = ({model: {id, title, formatted: {price}}}: { model: WarrantyModel }) => {
    const {activeWarrantyId, onChange} = useWarrantyChange();

    return (
        <div className="services-checkbox warranty-services me-2">
            <Form.Check
                id={`servicesCheckbox-${id}`}
                type="checkbox"
                className="warranty-service-checkbox"
                label={
                    <div>
                        {title} - {price}
                        <span className="warranty-selected"></span>
                    </div>
                }
                aria-label={`${title} - ${price}`}
                checked={activeWarrantyId == id}
                onChange={() => onChange(id)}
            />
        </div>);
};

export const Modal = ({show, onHide}: { show: boolean, onHide: () => void }) => {
    const {isFurniture, isDigital} = useWarrantyModal();

    return (
        <BootstrapModal show={show}
                        onHide={onHide}
                        size="xl" centered
                        id="warranty-modal">
            <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>Product Protection</BootstrapModal.Title>
                <Button variant="link" className="p-0" onClick={onHide}>
                    <strong>Close</strong>
                </Button>
            </BootstrapModal.Header>
            <BootstrapModal.Body>
                <h5 className="text-danger text-center">*VALID IN US ONLY, NOT AVAILABLE IN CANADA</h5>
                {isDigital && (
                    <div id="warranty-electronics-program-terms">
                        <h5 className="text-center text-primary">CONSUMER ELECTRONICS ADH PROGRAM TERMS</h5>
                        <h5>What's covered?</h5>
                        <p>
                            The Consumer Electronics Mulberry Protection plan covers against accidental damage and more,
                            including the following:
                        </p>
                        <ul>
                            <li>Damage from cracks, breakage &amp; spills</li>
                            <li>Free repairs for mechanical &amp; electrical breakdowns</li>
                            <li>Fast replacements with free shipping &amp; no fees</li>
                            <li>Damage from power surges &amp; electrical failures</li>
                            <li>Product defects once the Return Period expires</li>
                        </ul>
                    </div>
                )}
                {isFurniture && (
                    <div id="warranty-furniture-program-terms">
                        <h5 className="text-center text-primary">INDOOR FURNITURE ADH PROGRAM TERMS</h5>
                        <h5>What's covered?</h5>
                        <p>The Indoor Furniture Mulberry Protection plan covers against accidental damage and more,
                            including the following: </p>
                        <ul>
                            <li>All stain types;</li>
                            <li>Nail polish remover damage;</li>
                            <li>Burns and heat marks that are not caused by a fire;</li>
                            <li>Punctures, cuts, tears, or rips;</li>
                            <li>Seam separation;</li>
                            <li>Broken zippers or buttons;</li>
                            <li>Failure of frames, panels, springs, mechanisms, motors, levers or hand wands, or power
                                or
                                remote cords used for sleeper, reclining, and inclining applications, if these
                                components
                                were covered by an original manufacturer's warranty and such warranty has expired;
                            </li>
                            <li>Failure of integral electrical components;</li>
                            <li>Lifting or incident-specific chipping of veneers or laminates exposing the substrate;
                            </li>
                            <li>Liquid marks and rings;</li>
                            <li>Breakage, scratches, gouges, dents or chips;</li>
                            <li>Checking, cracking, bubbling, or peeling of the finish;</li>
                            <li>Chips, scratches, or breakage of glass or mirrors;</li>
                            <li>Loss of silvering on mirrors;</li>
                            <li>Warping;</li>
                            <li>Fading from the sun;</li>
                            <li>Breakage of hinges, handles, draw glides, pulls, nail-head trim;</li>
                            <li>Pet damage - single incident only.</li>
                        </ul>
                    </div>
                )}
                <h5 className="text-center text-primary">FREQUENTLY ASKED QUESTIONS</h5>
                <strong>What is the Mulberry ADH Program?</strong>
                <p>
                    Mulberry ADH (Accidental Damage &amp; Handling) offers additional product protection that covers
                    against accidental damage and more. This protection runs concurrently with any Manufacturer's
                    warranty so you can be confident that your product is protected from all of life's messes, for years
                    to come.
                </p>
                <strong>How do I purchase Mulberry Protection?</strong>
                <p>There are 2 ways to purchase Mulberry Protection:</p>
                <ol>
                    <li><u>Web Purchase</u>: When purchasing an eligible product on the D2G website, simply add the
                        desired Mulberry Protection to your cart with your eligible products and check out.
                    </li>
                    <li><u>Contact Center Purchase</u>: When purchasing an eligible product with one of our Contact
                        Center Agents, you can choose to have them add the Mulberry Protection to your order at that
                        time.
                    </li>
                </ol>
                <p>Once you purchase coverage, you will receive an email from Mulberry within 24 hours with a link to
                    create your account and access to your dedicated Mulberry dashboard, which you can use to manage
                    your plan, file a claim, and contact their team.</p>
                <strong>Can I cancel my Mulberry Protection Plan?</strong>
                <p>You may cancel your Mulberry Protection Plan within 30 days of purchase by
                    contacting {catalogName} at <a href="tel:1-800-572-2194" tabIndex={0} role="link" aria-label="link to telephone number">1-800-572-2194</a>. If there has been no
                    claim filed against the plan, the plan will be void and you will receive a full refund.</p>
                <strong>How do I file a claim?</strong>
                <p>Simply log in to your dedicated Mulberry dashboard at <a href="https://www.getmulberry.com/login"
                                                                            target="_blank">getmulberry.com/login</a> for
                    a 1-click, receipt-less claims filing process. You can also contact the Mulberry customer support
                    team via <a href="mailto:support@getmulberry.com" target="_blank" tabIndex={0} role="link" aria-label="Link to email">support@getmulberry.com</a>.</p>
                <strong>What happens if I return or exchange my products? </strong>
                <p>Each Mulberry Protection plan is tied to the specific product with which it was purchased. If you
                    return or exchange your product, you will need to contact D2G directly at <a
                        href="tel:1-844-977-3212">1-844-977-3212</a> to cancel the warranty and receive a refund.</p>
                <strong className="text-center text-primary">FULL CONTRACT TERMS</strong>
                <p>See the full contract terms <a href="https://terms.getmulberry.com/20210210_shield_multiproduct"
                                                  target="_blank" tabIndex={0} role="link" aria-label="Link to view contract terms">here</a>.</p>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </BootstrapModal.Footer>
        </BootstrapModal>
    );
};